import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { PpmMilestonesRepositoryFactory } from '@/store/ppm-state/milestones-state/milestones.repository';
import { PpmMilestoneProps } from '@/store/ppm-state/milestones-state/milestones.store';
import { PpmRepository } from '@/store/ppm-state/ppm-state.repository';
import { PpmMilestone, PpmMilestoneDto } from '@/types';
import { form } from '@/utils/form.utils';
import {
  ppmMilestoneDraftValidationSchema,
  ppmMilestoneFloawableValidationSchema,
  ppmMilestoneFundingValidationSchema,
  ppmMilestoneSlotsValidationSchema,
} from '@/validations/ppm/ppm-milestone.validation';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PpmMilestonesService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);
  ppmRepository = inject(PpmRepository);
  messageService = inject(MessageService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToMilestoneStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
        this.calculateTotalEstimatedBudget();
      })
    );
    this.subscribeToStores();
  }

  subscribeToMilestoneStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe(() => {
        this.calculateTotalEstimatedBudget();
      });
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe(() => {
      this.calculateTotalEstimatedBudget();
    });
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToMilestoneStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
        this.calculateTotalEstimatedBudget();
      })
    );
    this.storeSubscriptions.push(
      this.multiFormAccordionService.storeRemoved$.subscribe(() => {
        this.calculateTotalEstimatedBudget();
      })
    );
  }

  calculateTotalEstimatedBudget() {
    const total = this.multiFormAccordionService.getAllStores().reduce((sum, store) => {
      return sum + ((store.state as PpmMilestoneProps)?.milestoneDto?.budgetEstime ?? 0);
    }, 0);
    this.ppmRepository.set('totalEstimatedBudget', total);
  }

  getAllMilestoneDtos() {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<PpmMilestoneProps>): PpmMilestoneDto => {
      const repo = PpmMilestonesRepositoryFactory.create(store);
      return repo.get('milestoneDto');
    });
  }

  validateSlots(milestoneDto: PpmMilestoneDto, maxBudget = 0) {
    let isValid = true;
    const lotsErrors: Record<string, string[]>[] = [];
    const lots = milestoneDto.lots || [];

    // No slots present, considered valid
    if (lots.length === 0) {
      return { lotsErrors, isValid };
    }

    let budgetCount = 0;
    let definedBudgets = 0;
    let totalBudget = 0;

    lots.forEach((lot, i) => {
      const result = ppmMilestoneSlotsValidationSchema.safeParse(lot);
      if (!result.success) {
        isValid = false;
        lotsErrors[i] = result.error.flatten().fieldErrors;
      } else {
        lotsErrors[i] = {};
      }

      if (typeof lot.budget === 'number') {
        definedBudgets++;
        totalBudget += lot.budget;
      }

      budgetCount++;
    });

    // Vérification de la définition partielle des budgets
    if (definedBudgets > 0 && definedBudgets < budgetCount) {
      isValid = false;
      for (let i = 0; i < budgetCount; i++) {
        if (typeof lots[i].budget !== 'number') {
          lotsErrors[i] = {
            ...lotsErrors[i],
            budget: ['Le budget est requis si au moins un budget est défini.'],
          };
        }
      }
    }

    // Vérification de la somme des budgets
    if (definedBudgets === budgetCount && totalBudget !== maxBudget) {
      isValid = false;
      for (let i = 0; i < budgetCount; i++) {
        lotsErrors[i] = {
          ...lotsErrors[i],
          budget: ["la somme des budgets des lots ne correspond pas au budget estimé de l'activité"],
        };
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Le total des budgets doit être égal au budget autorisé.',
      });
    }

    return { lotsErrors, isValid };
  }

  validateFundings(milestoneDto: PpmMilestoneDto) {
    let isValid = true;
    const fundingsErrors: Record<string, string[]>[] = [];
    const fundings = milestoneDto.financements || [];

    if (fundings.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez ajouter au moins un financement',
      });
      isValid = false;
      return { fundingsErrors, isValid };
    }

    fundings.forEach((funding, i) => {
      const result = ppmMilestoneFundingValidationSchema.safeParse(funding);
      if (!result.success) {
        isValid = false;
        fundingsErrors[i] = result.error.flatten().fieldErrors;
      } else {
        fundingsErrors[i] = {};
      }
    });

    return { fundingsErrors, isValid };
  }

  validateAllMilestoneDtos(mode: 'draft' | 'flowable', minDate?: Date, actualizedCount = 0): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<PpmMilestoneProps>, index) => {
      const repo = PpmMilestonesRepositoryFactory.create(store);
      const milestoneDto = repo.get<PpmMilestoneDto>('milestoneDto');

      const milestoneRep = repo.get<PpmMilestone>('milestone');
      const result =
        mode == 'draft' || milestoneRep.usedInDac
          ? ppmMilestoneDraftValidationSchema(minDate).safeParse(milestoneDto)
          : ppmMilestoneFloawableValidationSchema(minDate).safeParse(milestoneDto);
      const slotsResult = this.validateSlots(milestoneDto, milestoneDto.budgetEstime);
      const fundingsResult = this.validateFundings(milestoneDto);

      // Validate comment is required for modified milestones (not for new ones)
      const commentErrors: Record<string, string[]> = {};
      const isModified = this.isMilestoneModified(store);
      if (isModified && !milestoneDto.comment?.trim() && actualizedCount > 0) {
        commentErrors['comment'] = ['Le commentaire est obligatoire pour les réalisations modifiées'];
        isValid = false;
      }

      if (!result.success || !slotsResult.isValid || !fundingsResult.isValid || Object.keys(commentErrors).length > 0) {
        isValid = false;

        const fieldErrors = result.success ? {} : result.error.flatten().fieldErrors;
        repo.set('errors', { ...fieldErrors, ...commentErrors });
        repo.set('slotErrors', slotsResult.lotsErrors);
        repo.set('fundingErrors', fundingsResult.fundingsErrors);

        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setMilestoneDto(response: PpmMilestone): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response?.id.toString());
    if (!newStore) {
      console.error(`Could not create store for milestone with id: ${response.id}`);
      return;
    }

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = PpmMilestonesRepositoryFactory.create(newStore);
    repo.setMilestoneDto(response);
  }

  setMilestoneDtos(responses: PpmMilestone[]): void {
    responses.forEach(r => {
      this.setMilestoneDto(r);
    });
  }

  isMilestoneNew(store: ElfBaseStore<PpmMilestoneProps>): boolean {
    const repo = PpmMilestonesRepositoryFactory.create(store);
    const initial = repo.get<PpmMilestoneDto>('initialMilestoneDto');
    return !initial;
  }

  isMilestoneModified(store: ElfBaseStore<PpmMilestoneProps>): boolean {
    const repo = PpmMilestonesRepositoryFactory.create(store);
    const current = repo.get<PpmMilestoneDto>('milestoneDto');
    const initial = repo.get<PpmMilestoneDto>('initialMilestoneDto');

    if (!initial) return false; // New milestones are not considered modified

    const currentWithout = _.omit(current, ['budgetEstime']);
    const initialWithout = _.omit(initial, ['budgetEstime']);

    return !_.isEqual(currentWithout, initialWithout);
  }

  altered(): boolean {
    return this.multiFormAccordionService.getAllStores().some(store => {
      const repo = PpmMilestonesRepositoryFactory.create(store);
      const current = repo.get<PpmMilestoneDto>('milestoneDto');
      const initial = repo.get<PpmMilestoneDto>('initialMilestoneDto');

      if (!initial) return true;

      const currentWithout = _.omit(current, ['budgetEstime']);
      const initialWithout = _.omit(initial, ['budgetEstime']);

      return !_.isEqual(currentWithout, initialWithout);
    });
  }

  clearAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = PpmMilestonesRepositoryFactory.create(store);
      repo.setNested(attribute, undefined);
    });
  }

  clearNestedDtoAttribute(attribute: string) {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = PpmMilestonesRepositoryFactory.create(store);
      repo.setNested('milestoneDto', attribute, undefined);
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
